import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Ref } from '@eastsideco/salvo-ts';

interface CompareSectionConfig {
    transitionDuration: number;
}

export class CompareSection {
    private readonly container: HTMLElement;
    private readonly beforeImage: HTMLElement;
    private readonly afterImage: HTMLElement;
    private readonly afterLabel: HTMLElement;
    private readonly handle: HTMLElement;
    private readonly rangeInput: HTMLInputElement;
    private readonly beforeOverlay: HTMLElement | null;
    private readonly afterOverlay: HTMLElement | null;
    private readonly config: CompareSectionConfig;

    private isDragging = false;
    private currentValue = 70; // 0-100 slider value

    constructor(container: HTMLElement, config?: Partial<CompareSectionConfig>) {
        this.container = container;
        this.config = {
            transitionDuration: 300,
            ...config
        };

        // Get required elements
        this.beforeImage = this.getElement('.compare-section__image-before');
        this.afterImage = this.getElement('.compare-section__image-after');
        this.handle = this.getElement('.compare-section__handle');
        this.rangeInput = this.getElement('.compare-section__handle-input') as HTMLInputElement;
        this.beforeOverlay = this.container.querySelector('.compare-section__image-overlay-before');
        this.afterOverlay = this.container.querySelector('.compare-section__image-overlay-after');
        this.afterLabel = this.getElement('.compare-section__label-after');

        if (!this.beforeImage || !this.afterImage || !this.handle || !this.rangeInput) {
            throw new Error('Required compare slider elements not found');
        }

        this.init();
    }

    private getElement(selector: string): HTMLElement {
        const element = this.container.querySelector(selector) as HTMLElement;
        if (!element) {
            throw new Error(`Element not found: ${selector}`);
        }
        return element;
    }

    private init(): void {
        this.setupEventListeners();
        this.updateSlider(this.currentValue);
        // Set initial overlay state
        this.updateOverlay(this.currentValue);
    }

    private setupEventListeners(): void {
        // Range input events
        this.rangeInput.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            this.currentValue = parseInt(target.value, 10);
            this.updateSlider(this.currentValue);
        });

        // Container click for positioning
        this.container.addEventListener('click', (e) => {
            if (e.target === this.rangeInput) {return;} // Don't interfere with range input

            const rect = this.container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const percentage = (x / rect.width) * 100;
            this.currentValue = Math.max(0, Math.min(100, percentage));
            this.rangeInput.value = this.currentValue.toString();
            this.updateSlider(this.currentValue);
        });

        // Handle drag events
        let startX = 0;
        let startValue = 0;

        const onMouseDown = (e: MouseEvent) => {
            this.isDragging = true;
            startX = e.clientX;
            startValue = this.currentValue;
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            e.preventDefault();
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!this.isDragging) {return;}

            const rect = this.container.getBoundingClientRect();
            const deltaX = e.clientX - startX;
            const deltaPercentage = (deltaX / rect.width) * 100;
            this.currentValue = Math.max(0, Math.min(100, startValue + deltaPercentage));
            this.rangeInput.value = this.currentValue.toString();
            this.updateSlider(this.currentValue);
        };

        const onMouseUp = () => {
            this.isDragging = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        // Touch events
        const onTouchStart = (e: TouchEvent) => {
            this.isDragging = true;
            startX = e.touches[0].clientX;
            startValue = this.currentValue;
            document.addEventListener('touchmove', onTouchMove, { passive: false });
            document.addEventListener('touchend', onTouchEnd);
            e.preventDefault();
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!this.isDragging) {return;}

            const rect = this.container.getBoundingClientRect();
            const deltaX = e.touches[0].clientX - startX;
            const deltaPercentage = (deltaX / rect.width) * 100;
            this.currentValue = Math.max(0, Math.min(100, startValue + deltaPercentage));
            this.rangeInput.value = this.currentValue.toString();
            this.updateSlider(this.currentValue);
            e.preventDefault();
        };

        const onTouchEnd = () => {
            this.isDragging = false;
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };

        this.handle.addEventListener('mousedown', onMouseDown);
        this.handle.addEventListener('touchstart', onTouchStart);
    }

    private updateSlider(value: number): void {
        const percentage = value;

        // Disable transitions during dragging for better performance
        if (this.isDragging) {
            this.beforeImage.style.transition = 'none';
            this.afterImage.style.transition = 'none';
            this.handle.style.transition = 'none';
            this.afterLabel.style.transition = 'none';
            if (this.beforeOverlay) {this.beforeOverlay.style.transition = 'none';}
            if (this.afterOverlay) {this.afterOverlay.style.transition = 'none';}
        } else {
            this.beforeImage.style.transition = '';
            this.afterImage.style.transition = '';
            this.handle.style.transition = '';
            this.afterLabel.style.transition = '';
            if (this.beforeOverlay) {this.beforeOverlay.style.transition = '';}
            if (this.afterOverlay) {this.afterOverlay.style.transition = '';}
        }

        // Update handle position
        this.handle.style.left = `${percentage}%`;
        this.afterLabel.style.left = `${percentage}%`;

        // Update clip paths for both images
        this.beforeImage.style.clipPath = `polygon(0 0, ${percentage}% 0, ${percentage}% 100%, 0 100%)`;
        this.afterImage.style.clipPath = `polygon(${percentage}% 0, 100% 0, 100% 100%, ${percentage}% 100%)`;

        // Update label visibility based on dominant image
        this.updateOverlay(percentage);
    }

    private updateOverlay(percentage: number): void {
        if (!this.beforeOverlay || !this.afterOverlay) {return;}

        // Calculate opacity based on slider position for smoother transitions
        const beforeOpacity = Math.max(0, Math.min(1, (50 - percentage) / 50));
        const afterOpacity = Math.max(0, Math.min(1, (percentage - 50) / 50));

        // Apply opacity directly for smooth transitions
        this.beforeOverlay.style.opacity = beforeOpacity.toString();
        this.afterOverlay.style.opacity = afterOpacity.toString();

        // Update pointer events based on visibility
        this.beforeOverlay.style.pointerEvents = beforeOpacity > 0.1 ? 'auto' : 'none';
        this.afterOverlay.style.pointerEvents = afterOpacity > 0.1 ? 'auto' : 'none';
    }

    public destroy(): void {
        // Remove event listeners (cleanup would be handled by removing elements)
        // This is a minimal implementation for now
    }
}
// Auto-initialization
if (typeof document !== 'undefined') {
    const initializeSliders = () => {
        const selectors = ['.section-compare-section', '[data-compare-section]'];
        const containers = selectors.flatMap((selector) =>
            Array.from(document.querySelectorAll(selector))
        );

        for (const container of containers) {
            try {
                new CompareSection(container as HTMLElement);
            } catch (error) {
                // Silently handle initialization errors to avoid console pollution
                // In production, you might want to log to a service instead
            }
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeSliders);
    } else {
        initializeSliders();
    }
}

// Global access for dynamic initialization
if (typeof window !== 'undefined') {
    (window as unknown as { compareSection: typeof CompareSection }).compareSection = CompareSection;
}

